// FILE: src/services/authReturn.js
//
// Keeps a deep link alive across an OAuth round trip.
//
// Google/Microsoft sign-in leaves the app and comes back through Supabase, so
// the requested destination has to survive the redirect. Two belts:
//   1. the redirect URL itself carries the destination (authRedirectUrl), and
//   2. the destination is stashed in sessionStorage before we leave, so the
//      link still resolves if the provider or the allow-list drops the query.
// Only the app's own view params are ever kept — never a full arbitrary URL.
const KEY = 'pmPixel.authReturn';

/** Params worth returning to after sign-in. */
const KEEP = ['view', 'session', 'cert', 'from'];

function currentReturnParams() {
  const url = new URL(window.location.href);
  const keep = new URLSearchParams();
  KEEP.forEach((k) => {
    const v = url.searchParams.get(k);
    if (v) keep.set(k, v);
  });
  return keep;
}

/** Absolute URL Supabase should send the learner back to. */
export function authRedirectUrl() {
  if (typeof window === 'undefined') return undefined;
  try {
    const keep = currentReturnParams();
    keep.set('auth', 'callback');
    return `${window.location.origin}/?${keep.toString()}`;
  } catch (_) {
    return `${window.location.origin}/?auth=callback`;
  }
}

/** Remember the current destination just before leaving for the provider. */
export function stashAuthReturn() {
  if (typeof window === 'undefined') return;
  try {
    const keep = currentReturnParams();
    const qs = keep.toString();
    if (qs) window.sessionStorage.setItem(KEY, qs);
    else window.sessionStorage.removeItem(KEY);
  } catch (_) { /* storage unavailable — the redirect URL still carries it */ }
}

/**
 * On return from the provider, put the requested destination back in the
 * address bar if the redirect lost it. Safe to call on every load; it only
 * acts on an `auth=callback` landing that has no view of its own.
 *
 * @returns {boolean} true when the URL was restored
 */
export function consumeAuthReturn() {
  if (typeof window === 'undefined') return false;
  try {
    const url = new URL(window.location.href);
    const stashed = window.sessionStorage.getItem(KEY);
    if (stashed) window.sessionStorage.removeItem(KEY);
    if (url.searchParams.get('auth') !== 'callback') return false;
    if (url.searchParams.get('view')) return false;   // destination survived
    if (!stashed) return false;
    const restored = new URLSearchParams(stashed);
    restored.set('auth', 'callback');
    // Keep the fragment: on an implicit-flow return it carries the tokens the
    // Supabase client is about to read. Dropping it would land the learner on
    // the right page, signed out.
    window.history.replaceState(
      window.history.state, '', `/?${restored.toString()}${window.location.hash || ''}`,
    );
    return true;
  } catch (_) {
    return false;
  }
}
